/**
 * .fadein container = a ZIP archive (deflate) with exactly one entry,
 * `document.xml` (UTF-8). This module is the only place that knows about the
 * zip envelope; everything else works with the XML string.
 */
import { unzipSync, zipSync, strToU8, strFromU8 } from "fflate";

export const DOCUMENT_ENTRY = "document.xml";

/** Thrown when the archive is not a recognizable .fadein container. */
export class FadeinContainerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FadeinContainerError";
  }
}

/** Unzip a .fadein file and return the `document.xml` text (UTF-8 decoded). */
export function readFadein(data: ArrayBuffer | Uint8Array): string {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes);
  } catch (err) {
    throw new FadeinContainerError(
      `Not a valid .fadein archive: ${(err as Error).message}`,
    );
  }
  const entry = entries[DOCUMENT_ENTRY];
  if (!entry) {
    const found = Object.keys(entries).join(", ") || "(empty)";
    throw new FadeinContainerError(
      `Archive is missing ${DOCUMENT_ENTRY} (found: ${found})`,
    );
  }
  return strFromU8(entry);
}

/**
 * Wrap an XML string into a single-entry deflated .fadein archive.
 * Nothing else is written — exactly one entry named `document.xml`.
 */
export function writeFadein(xml: string): Uint8Array {
  return zipSync(
    { [DOCUMENT_ENTRY]: strToU8(xml) },
    { level: 6 },
  );
}
